/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

/**
 * @typedef choice
 * @property {String} key
 * @property {String} content
 */

/**
 * Collection of helper functions which provide information about choices and pairs
 * @param {choice[][]} choices - static value, originally defined in itemData
 * @returns {Object} API — each method takes the current `pairs` array as its first argument
 */
export default function MatchChoicesManagerFactory(choices) {
    return {
        /**
         * Get all choices from choices[0] who are currently paired with a given key from choices[1]
         * @param {[string, string][]} pairs
         * @param {String} yKey
         * @returns {choice[]}
         */
        getChoicesPairedWithKey(pairs, yKey) {
            return choices[0].filter(choice => this.areKeysPaired(pairs, choice.key, yKey));
        },

        /**
         * Get the empty item placeholders to render in a bucket
         * @param {[string, string][]} pairs
         * @param {choice} bucket
         * @param {String} bucket.key
         * @param {Number} bucket.matchMax
         * @returns {choice[]}
         */
        getPlaceholderChoices(pairs, { key, matchMax = 0 }) {
            const choicesAbsent = choices[0].filter(choice => !this.areKeysPaired(pairs, choice.key, key));
            if (matchMax > 0) {
                const membersAmount = this.getChoiceYUsageCount(pairs, key);
                const placeholdersAmount = Math.max(0, matchMax - membersAmount);
                return choicesAbsent.slice(0, placeholdersAmount);
            }
            return choicesAbsent;
        },

        /**
         * Get all choices from choices[0], and sort to the top those who remain not all used
         * @param {[string, string][]} pairs
         * @param {Boolean} minify - remove all absent choices from list, except 1
         * @returns {choice[]}
         */
        getSortedUnusedChoices(pairs, minify = false) {
            const choicesPresent = choices[0].filter(choice => this.getChoiceXStackSize(pairs, choice.key) !== 0);
            let choicesAbsent = choices[0].filter(choice => this.getChoiceXStackSize(pairs, choice.key) === 0);
            if (minify && choicesAbsent.length > 1) {
                choicesAbsent = choicesAbsent.pop();
            }
            return choicesPresent.concat(choicesAbsent);
        },

        /**
         * Count choiceX usage in pairs
         * @param {[string, string][]} pairs
         * @param {String} xKey
         * @returns {Number}
         */
        getChoiceXUsageCount(pairs, xKey) {
            return pairs.filter(pair => pair[0] === xKey).length;
        },

        /**
         * Count choiceY usage in pairs
         * @param {[string, string][]} pairs
         * @param {String} yKey
         * @returns {Number}
         */
        getChoiceYUsageCount(pairs, yKey) {
            return pairs.filter(pair => pair[1] === yKey).length;
        },

        /**
         * Get the number of copies of a choice remaining in choices list ("Set A")
         * @param {[string, string][]} pairs
         * @param {String} xKey
         * @returns {Number}
         */
        getChoiceXStackSize(pairs, xKey) {
            const thisChoice = choices[0].find(choice => choice.key === xKey);
            if (thisChoice.matchMax === 0) {
                return -1; // unlimited stack
            }
            return thisChoice.matchMax - this.getChoiceXUsageCount(pairs, thisChoice.key);
        },

        /**
         * Count total items remaining in choices list ("Set A")
         * @param {[string, string][]} pairs
         * @returns {Number}
         */
        getChoicesPresentItemCount(pairs) {
            return choices[0].reduce((count, choice) => {
                if (choice.matchMax === 0 || choice.matchMax > this.getChoiceXUsageCount(pairs, choice.key)) {
                    return count + 1;
                }
                return count;
            }, 0);
        },

        /**
         * Check if keys x (from "Set A") and y (from "Set B") have been connected as a pair
         * @param {[string, string][]} pairs
         * @param {String} xKey
         * @param {String} yKey
         * @returns {Boolean} true if pair exists
         */
        areKeysPaired(pairs, xKey, yKey) {
            return Boolean(pairs.find(pair => pair[0] === xKey && pair[1] === yKey));
        },

        /**
         * Check if a bucket (yKey) can receive a choice (xKey) based on capacity and current pairs
         * @param {[string, string][]} pairs
         * @param {String} xKey
         * @param {String} yKey
         * @returns {Boolean} true if can receive
         */
        canReceiveChoice(pairs, xKey, yKey) {
            const bucket = choices[1].find(choice => choice.key === yKey);
            const full = bucket.matchMax > 0 && this.getChoiceYUsageCount(pairs, yKey) >= bucket.matchMax;
            return !full && !this.areKeysPaired(pairs, xKey, yKey);
        },

        /**
         * Check if the given bucket (entry in "Set B") is valid according to its own constraints
         * @param {[string, string][]} pairs
         * @param {choice} bucket
         * @param {String} bucket.key
         * @param {Number} bucket.matchMax
         * @param {Number} bucket.matchMin
         * @returns {Boolean} true if valid
         */
        isValidBucket(pairs, { key, matchMax, matchMin }) {
            const members = this.getChoiceYUsageCount(pairs, key);
            const tooMany = matchMax > 0 && members > matchMax;
            const tooFew = matchMin > 0 && members < matchMin;
            return !tooMany && !tooFew;
        },

        /**
         * Check if max associations is reached, and adding new ones will exceed the constraint
         * @param {[string, string][]} pairs
         * @param {Number} maxAssociations
         * @returns {Boolean} true if reached
         */
        reachedMaxAssociations(pairs, maxAssociations) {
            return maxAssociations > 0 && pairs.length >= maxAssociations;
        },

        /**
         * Check if any of the choices has matchMax defined
         * @returns {boolean}
         */
        hasMatchMaxChoices() {
            return choices[0].some(ch => ch.matchMax > 0) || choices[1].some(ch => ch.matchMax > 0);
        },

        /**
         * Check max possible number of associations, ignoring the constraints (matrix size)
         * @returns {Number}
         */
        getPossibleAssociationsCount() {
            return choices[0].length * choices[1].length;
        }
    };
}
